import type { ComponentProps } from 'react';
import { ChevronLeft, Search } from 'lucide-react';
import { CommandEmpty, CommandList } from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ActionsPage } from './pages/actions-page';
import { RootPage } from './pages/root-page';
import { ScopePage } from './pages/scope-page';
import { ProductFolderPicker } from './pickers/product-folder-picker';
import { ProjectPicker } from './pickers/project-picker';
import { StaticPicker } from './pickers/static-picker';
import { CommandRows } from './sections';
import { OpenTabRows } from './open-tab-rows';
import { pageKey, type MenuPage } from './types';
import type { GlobalCommandMenuCopy } from './global-command-menu-copy';

interface GlobalCommandMenuContentProps {
  page: MenuPage;
  stackDepth: number;
  text: GlobalCommandMenuCopy;
  breadcrumbLabel: string | null;
  showEmptyState: boolean;
  openTabs: ComponentProps<typeof OpenTabRows>;
  pageActions: ComponentProps<typeof CommandRows>;
  rootPage: ComponentProps<typeof RootPage>;
  scopePage: ComponentProps<typeof ScopePage>;
  actionsPage: ComponentProps<typeof ActionsPage>;
  term: string;
  orgSlug?: string;
  close: () => void;
  popPage: () => void;
}

export function GlobalCommandMenuContent({
  page,
  stackDepth,
  text,
  breadcrumbLabel,
  showEmptyState,
  openTabs,
  pageActions,
  rootPage,
  scopePage,
  actionsPage,
  term,
  orgSlug,
  close,
  popPage,
}: GlobalCommandMenuContentProps) {
  return (
    <CommandList className="max-h-none min-h-0 overflow-hidden">
      <ScrollArea
        className="w-full max-w-full"
        maskClassName="dark:before:from-popover dark:after:from-popover"
        viewportClassName="max-h-[min(400px,50vh)] scroll-py-1"
      >
        {stackDepth > 0 ? (
          <div className="border-border/40 border-b px-3 py-2">
            <button
              type="button"
              onClick={popPage}
              className="text-muted-foreground/70 hover:text-foreground inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
            >
              <ChevronLeft className="size-3.5" />
              <span>{text.backToAll}</span>
              {breadcrumbLabel ? (
                <span className="text-muted-foreground/40 ml-1">
                  / {breadcrumbLabel}
                </span>
              ) : null}
            </button>
          </div>
        ) : null}

        {showEmptyState ? (
          <CommandEmpty>
            <div className="flex flex-col items-center gap-2 py-6">
              <Search className="text-muted-foreground/40 size-5" />
              <p className="text-muted-foreground text-sm">
                {text.noResultsFound}
              </p>
            </div>
          </CommandEmpty>
        ) : null}

        <div key={pageKey(page)}>
          {page.kind === 'root' ? <OpenTabRows {...openTabs} /> : null}
          {(page.kind === 'root' || page.kind === 'actions') &&
          pageActions.commands.length > 0 ? (
            <CommandRows {...pageActions} />
          ) : null}
          {page.kind === 'root' ? <RootPage {...rootPage} /> : null}
          {page.kind === 'scope' ? (
            <ScopePage {...scopePage} scope={page.scope} />
          ) : null}
          {page.kind === 'actions' ? <ActionsPage {...actionsPage} /> : null}
          {page.kind === 'picker' && page.subPage.type === 'project-picker' ? (
            <ProjectPicker
              term={term}
              heading={page.label}
              noneLabel={page.subPage.noneLabel}
              emptyLabel={text.noResultsFound}
              orgSlug={orgSlug}
              onPick={page.subPage.onPick}
              onDone={close}
            />
          ) : null}
          {page.kind === 'picker' &&
          page.subPage.type === 'product-folder-picker' ? (
            <ProductFolderPicker
              term={term}
              heading={page.label}
              noneLabel={page.subPage.noneLabel}
              emptyLabel={text.noResultsFound}
              orgSlug={orgSlug}
              onPick={page.subPage.onPick}
              onDone={close}
            />
          ) : null}
          {page.kind === 'picker' && page.subPage.type === 'static' ? (
            <StaticPicker
              term={term}
              heading={page.label}
              items={page.subPage.items}
              emptyLabel={text.noResultsFound}
              onDone={close}
            />
          ) : null}
        </div>
      </ScrollArea>
    </CommandList>
  );
}
